'use client';

import type { ComponentPropsWithRef, ForwardedRef, KeyboardEvent } from 'react';

import type { DataAttributesProps } from '../../../utils/types';
import type { SelectOption } from '../common';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { useDebounceCallback } from '@polyseme/utils/hooks/use-debounce-callback/use-debounce-callback';
import { Command as CommandPrimitive } from 'cmdk';
import { Check } from 'lucide-react';
import mergeRefs from 'merge-refs';
import { forwardRef, useCallback, useRef, useState } from 'react';

import { CommandGroup, CommandItem, CommandList } from '../../../ui/command';
import { TextInput } from '../text-input';

/**
 * Props for the Autocomplete component.
 *
 * Search-as-you-type input with a dropdown of matching options.
 * Supports keyboard navigation (Enter to select, Escape to close) via cmdk CommandPrimitive.
 * ARIA attributes are handled automatically by the underlying CommandPrimitive.
 */
type AutocompleteProps = DataAttributesProps &
  Omit<ComponentPropsWithRef<'input'>, 'onChange' | 'value'> & {
    disabled?: boolean;
    /** Message displayed when search returns no results. */
    emptyMessage?: string;
    /** When true, shows a loading spinner in the input. */
    isLoading?: boolean;
    listClassName?: string;
    /** Minimum number of characters before triggering a search. @default 3 */
    minInputLength?: number;
    /** Called when the input text changes. Debounced internally (200ms). */
    onChange: (value?: string) => void;
    /** Called when the user selects an option from the dropdown. Receives the option value. */
    onSelectOption: (value: string) => void;
    /** List of selectable options displayed in the dropdown. */
    options?: SelectOption[];
    placeholder?: string;
    value?: string;
  };

export const Autocomplete = forwardRef(
  (
    {
      className,
      'data-testid': dataTestId,
      disabled,
      emptyMessage = 'Aucun résultat',
      isLoading = false,
      listClassName,
      minInputLength = 3,
      onChange,
      onSelectOption,
      options,
      placeholder,
      value,
      ...props
    }: AutocompleteProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const testId = dataTestId ?? props.name;
    const inputRef = useRef<HTMLInputElement>(null);

    const [isOpen, setOpen] = useState(false);
    const [selected, setSelected] = useState<SelectOption>();
    const [inputValue, setInputValue] = useState<string>(value || '');

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        const input = inputRef.current;
        if (!input) {
          return;
        }

        // Keep the options displayed when the user is typing
        if (!isOpen) {
          setOpen(true);
        }

        // This is not a default behaviour of the <input /> field
        if (event.key === 'Enter' && input.value !== '') {
          const selectedOption = options?.find(
            (option) => option.label === input.value
          );
          if (selectedOption) {
            setSelected(selectedOption);
            onSelectOption(selectedOption.value);
          }
        }

        if (event.key === 'Escape') {
          input.blur();
        }
      },
      [isOpen, options, onSelectOption]
    );

    const handleBlur = useCallback(() => {
      setOpen(false);
      if (inputValue) {
        setInputValue(selected?.label || inputValue);
      } else {
        onChange(undefined);
      }
    }, [inputValue, onChange, selected?.label]);

    const handleSelectOption = useCallback(
      (selectedOption: SelectOption) => {
        setInputValue(selectedOption.label);
        setSelected(selectedOption);
        onSelectOption(selectedOption.value);

        // This is a hack to prevent the input from being focused after the user selects an option
        // We can call this hack: "The next tick"
        setTimeout(() => {
          inputRef?.current?.blur();
        }, 0);
      },
      [onSelectOption]
    );

    const debouncedOnChange = useDebounceCallback(
      (value?: string) => onChange(value || ''),
      200
    );

    const hasMinInputLength = inputValue.trim().length >= minInputLength;
    const isSearching = isLoading && hasMinInputLength;
    const hasSearched =
      !isLoading && hasMinInputLength && options !== undefined;

    return (
      <CommandPrimitive className={className} onKeyDown={handleKeyDown}>
        <div>
          <TextInput
            {...props}
            ref={mergeRefs(ref, inputRef)}
            data-testid={testId}
            disabled={disabled}
            placeholder={placeholder}
            rightIcon={isSearching ? 'loader-circle' : undefined}
            rightIconClassName="text-primary animate-spin"
            value={inputValue}
            onBlur={handleBlur}
            onChange={({ target: { value } }) => {
              setInputValue(value);
              debouncedOnChange(value);
            }}
            onFocus={() => setOpen(true)}
          />
        </div>
        <div className="relative mt-1">
          <div
            className={cn(
              'absolute top-1 z-10 w-full rounded-xl bg-popover outline-none animate-in fade-in-0 zoom-in-95',
              isOpen && hasSearched ? 'block border' : 'hidden',
              listClassName
            )}
          >
            <CommandList className="rounded-lg">
              {!!options?.length && !!hasSearched && (
                <CommandGroup>
                  {options.map((option, index) => {
                    const isSelected = selected?.value === option.value;
                    return (
                      <CommandItem
                        key={option.value}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-lg',
                          !isSelected ? 'pl-8' : null
                        )}
                        data-testid={`${testId}-${index}`}
                        value={option.label}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        onSelect={() => handleSelectOption(option)}
                      >
                        {isSelected ? <Check className="w-4" /> : null}
                        {option.label}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
              {!!hasSearched && (
                <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm">
                  {emptyMessage}
                </CommandPrimitive.Empty>
              )}
            </CommandList>
          </div>
        </div>
      </CommandPrimitive>
    );
  }
);

Autocomplete.displayName = 'Autocomplete';
